import React, { FunctionComponent, useEffect, useState } from "react";
import MaterialTable from "@material-table/core";

import {
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Snackbar,
  useMediaQuery
} from "@material-ui/core";

import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";

import Typography from "@material-ui/core/Typography";
import { useDispatch, useSelector } from "react-redux";
import { emptyProduct, Product } from "../../../../models/Product";
import { AppState } from "../../../../store";
import { thunkFetchProductsV2 } from "../../../../store/products/action";
import useStyles from "./styles";
import { StoreInventoryItemEditor } from "./StoreInventoryItemEditor";
import {
  MinimalStoreInventoryItem,
  StoreInventoryItem,
  toMinimalStoreInventoryItem
} from "../../../../models/StoreInventory";
import { Alert } from "@material-ui/lab";
import { useTheme } from "@material-ui/core/styles";
import { Currency } from "../../../../components/misc/Currency";
import { Store } from "../../../../models/Store";

import * as StoreInventoryApi from "../../../../api/storeInventoryApi";
import { isOk } from "../../../../result";
import { ConnectToPriceTag } from "../../../tenants/Products/ConnectToPriceTag";

interface AddProductListProps {
  storeId: string;
  currentInventoryItems: ReadonlyArray<StoreInventoryItem>;
  updateInventory: Function;
}

export const AddProductList: FunctionComponent<AddProductListProps> = ({
  storeId,
  currentInventoryItems,
  updateInventory
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const allStores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  const [state, setState] = useState<any>({
    storeId: storeId,
    editingStoreInventoryItem: emptyProduct(),
    editStoreInventoryItemDialogOpen: false,
    showProductAddedMessage: false,
    currentInventoryItems: currentInventoryItems
  });

  const products =
    useSelector<AppState, ReadonlyArray<Product>>(
      s => s.storeClusterProducts.productState.products
    ) || [];

  useEffect(() => {
    if (!products || !products.length) {
      dispatch(thunkFetchProductsV2());
    }
  }, [products]);

  const productsNotInInventory = (): any => {
    return products.filter(
      ({ id: id }) =>
        !state.currentInventoryItems.some(
          // @ts-ignore
          ({ productId: productId }) => productId === id
        )
    );
  };

  return (
    <div>
      <MaterialTable
        title=""
        isLoading={!products || !products.length}
        columns={[
          {
            title: "GTIN",
            field: "gtin",
            hidden: true,
            searchable: true
          },
          {
            title: "ArticleNumber",
            field: "articleNumber",
            hidden: true,
            searchable: true
          },
          {
            title: "Brand",
            field: "brandName",
            hidden: true,
            searchable: true
          },
          {
            title: "Product",
            field: "name",
            render: (row: Product) => {
              return (
                <div style={{ minWidth: 180 }}>
                  <Typography>{row.name}</Typography>
                  <Typography color="textSecondary">
                    {row.gtin}, {row.details?.size} {row.brandName}
                  </Typography>
                  {row.articleNumber && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      component="p"
                    >
                      Art.nbr {row.articleNumber}
                    </Typography>
                  )}
                </div>
              );
            }
          },
          {
            title: "Category",
            field: "category.name",
            hidden: mobile
          },
          {
            title: "Price",
            field: "pricing.price",
            hidden: mobile,
            render: row => {
              return (
                <div>
                  <Typography>
                    <Currency
                      value={row.pricing.price}
                      priceUnit={row.pricing.priceUnit}
                    />
                  </Typography>
                </div>
              );
            }
          }
        ]}
        data={
          products && products.length ? (productsNotInInventory() as []) : []
        }
        options={{
          pageSize: 20,
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" }
        }}
        actions={[
          {
            icon: "add_circle",
            tooltip: "Activate product in store",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            onClick: async (event, rowData: any) => {
              if (allStores.length === 1) {
                const store = allStores[0];

                const result = await StoreInventoryApi.add(store.id, {
                  productId: rowData.id,
                  gtin: rowData.gtin,

                  hasPriceTag: false
                });

                if (isOk(result)) {
                  const item = {
                    ...rowData,
                    productId: rowData.id,
                    productName: rowData.name
                  };
                  const items = [...state.currentInventoryItems, item];

                  setState({
                    ...state,
                    showProductAddedMessage: true,
                    currentInventoryItems: items
                  });
                  updateInventory(items);
                } else {
                  setState({ ...state, error: result.details.detail });
                }
              } else {
                setState({
                  ...state,
                  editStoreInventoryItemDialogOpen: true,
                  editingStoreInventoryItem: {
                    ...rowData,
                    price: rowData.pricing.price,
                    maxOnShelf: rowData.orderInfo?.maxOnShelf,
                    orderThreshold: rowData.orderInfo?.orderThreshold,
                    orderUnit: rowData.orderInfo?.orderUnit
                  }
                });
              }
            }
          },
          {
            icon: "link",
            tooltip: "Link product to price tag",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            onClick: async (event, rowData: any) => {
              const item = {
                ...rowData,
                productId: rowData.id,
                productName: rowData.name
              };
              const items = [...state.currentInventoryItems, item];

              setState({
                ...state,
                storeId: storeId,
                currentInventoryItems: items,
                showConnectToPriceTag: true,
                editingStoreInventoryItem: {
                  ...rowData
                }
              });
            }
          }
        ]}
        localization={{
          header: {
            actions: ""
          }
        }}
        components={{
          Container: props => <Paper {...props} elevation={0} />
        }}
      />

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.editStoreInventoryItemDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, editStoreInventoryItemDialogOpen: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <StoreInventoryItemEditor
            storeId={state.storeId}
            inventoryItem={toMinimalStoreInventoryItem(
              state.editingStoreInventoryItem
            )}
            onSave={(updatedItem: any) => {
              const item = {
                ...updatedItem,
                productId: state.editingStoreInventoryItem.id,
                productName: state.editingStoreInventoryItem.name
              };
              const items = [...state.currentInventoryItems, item];

              setState({
                ...state,
                editStoreInventoryItemDialogOpen: false,
                showProductAddedMessage: true,
                currentInventoryItems: items
              });
              updateInventory(items);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={state.showConnectToPriceTag}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showConnectToPriceTag: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ConnectToPriceTag
            addToStore={true}
            storeId={state.storeId}
            productId={state.editingStoreInventoryItem.id}
            gtin={state.editingStoreInventoryItem.gtin}
            onSuccess={() => {
              setState({
                ...state,
                showConnectToPriceTag: false,
                showProductAddedMessage: true
              });
              updateInventory(state.currentInventoryItems);
            }}
            onCancel={() =>
              setState({
                ...state,
                showConnectToPriceTag: false
              })
            }
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.showProductAddedMessage}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            showProductAddedMessage: false
          });
        }}
      >
        <Alert severity="success">
          Product "{state.editingStoreInventoryItem.name}" added to store
        </Alert>
      </Snackbar>

      <Snackbar
        open={state.addProductToStoreFailed}
        autoHideDuration={5000}
        onClose={() => {
          setState({
            ...state,
            addProductToStoreFailed: false
          });
        }}
      >
        <Alert severity="error">{state.error}</Alert>
      </Snackbar>
    </div>
  );
};
